// Pure helpers for the RMA module. No React, no `st` writes, no side effects — everything
// here is a plain function over data so it can be reused from App.js, the RMA screens and
// (eventually) tests without dragging component state along.
//
// Two things in here are deliberately shared beyond RMA:
//   - the warranty helpers, which replace the same `+12 months` expression that was
//     copy-pasted into five places across App.js;
//   - TONE, which maps a semantic tone name onto the CSS custom properties defined in
//     owl-bench-tokens.css / -light.css, so both themes work without per-screen branching.

const DAY_MS = 86400000;

// ── Warranty ────────────────────────────────────────────────────────────────────────────
// The term used to be hardcoded to 12 months in five separate places. It now lives in
// st.rmaSettings.warrantyMonths (defaulted by mergeState) and is threaded through as an
// argument, so a supplier changing terms is a settings edit rather than a code change.

export const DEFAULT_WARRANTY_MONTHS = 12;
export const WARRANTY_EXPIRING_DAYS = 30;

// A Date, or null when the device has no purchaseDate. Many import paths leave purchaseDate
// empty (App.js sets it to "" in several places), so null is the normal case, not an error.
export const warrantyEnd = (purchaseDate, months = DEFAULT_WARRANTY_MONTHS) => {
  if (!purchaseDate) return null;
  const start = new Date(purchaseDate);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start.getTime());
  end.setMonth(end.getMonth() + (Number(months) || DEFAULT_WARRANTY_MONTHS));
  return end;
};

// Whole days remaining. Negative once expired; null when unknown.
export const warrantyDaysLeft = (purchaseDate, months, now = Date.now()) => {
  const end = warrantyEnd(purchaseDate, months);
  if (!end) return null;
  return Math.ceil((end.getTime() - now) / DAY_MS);
};

// "none" (no purchase date) | "expired" | "expiring" (<30d) | "valid"
export const warrantyState = (purchaseDate, months, now = Date.now()) => {
  const end = warrantyEnd(purchaseDate, months);
  if (!end) return 'none';
  const left = end.getTime() - now;
  if (left < 0) return 'expired';
  if (left < WARRANTY_EXPIRING_DAYS * DAY_MS) return 'expiring';
  return 'valid';
};

// Display shape for the warranty pill. Matches the copy the two device panels already used
// ("Expired" / "Expires <date>" / "Valid until <date>") so this refactor is invisible.
export const warrantyLabel = (purchaseDate, months, now = Date.now()) => {
  const state = warrantyState(purchaseDate, months, now);
  const end = warrantyEnd(purchaseDate, months);
  const date = end ? end.toLocaleDateString('en-GB') : '';
  if (state === 'none') return { state, text: '—', pillClass: 'pill pill-neutral', tone: 'neutral', end: null, date: '' };
  if (state === 'expired') return { state, text: 'Expired', pillClass: 'pill pill-danger', tone: 'danger', end, date };
  if (state === 'expiring') return { state, text: 'Expires ' + date, pillClass: 'pill pill-warning', tone: 'warning', end, date };
  return { state, text: 'Valid until ' + date, pillClass: 'pill pill-success', tone: 'success', end, date };
};

// Compact form for dense tables: "9 mo left" / "24 d left" / "Out of warranty".
//
// The amber band runs to 90 days rather than the 30 used for the notification bell: on the
// RMA screens this number answers "is it worth raising a claim?", and a unit with two months
// left genuinely is urgent once shipping and the manufacturer's 30-day turnaround are
// counted. Green here has to mean "no rush", not merely "not expired yet".
export const WARRANTY_URGENT_DAYS = 90;
export const warrantyShort = (purchaseDate, months, now = Date.now()) => {
  const left = warrantyDaysLeft(purchaseDate, months, now);
  if (left === null) return { text: 'Unknown', tone: 'neutral' };
  if (left < 0) return { text: 'Out of warranty', tone: 'danger' };
  if (left <= WARRANTY_EXPIRING_DAYS) return { text: left + ' d left', tone: 'warning' };
  const mo = Math.round(left / 30);
  return { text: mo + ' mo left', tone: left <= WARRANTY_URGENT_DAYS ? 'warning' : 'success' };
};

// Was the unit in warranty at a specific past moment? Used for the `chargeable` flag, which
// must be judged at DISPATCH, not now — otherwise the "unit chargeable" callout on the RMA
// detail screen appears and disappears as time passes.
export const wasInWarrantyAt = (purchaseDate, atIso, months) => {
  const end = warrantyEnd(purchaseDate, months);
  if (!end || !atIso) return false;
  const at = new Date(atIso);
  if (isNaN(at.getTime())) return false;
  return end.getTime() > at.getTime();
};

// ── Stages ──────────────────────────────────────────────────────────────────────────────
// `stage` is stored on the shipment. Overdue is NOT a stage — it is computed (see below),
// because an overdue RMA is still "with manufacturer"; overdue is a property of how long
// it has been there.
//
// timelineLabel differs from label because the detail screen's timeline reads as a history
// of events ("Raised", "Dispatched") whereas the queue's pills read as states ("Awaiting
// dispatch", "With manufacturer"). Same key, two registers.
//
// There is deliberately no "in transit" stage: dispatching moves an RMA straight to
// with_manufacturer. A parcel in the post is not a state the bench can act on differently,
// so the tab only ever read 0. Records written before its removal are folded into
// with_manufacturer by stageOf() below.

export const RMA_STAGES = [
  { key: 'awaiting_dispatch', label: 'Awaiting dispatch', timelineLabel: 'Raised',              step: 1, tone: 'warning', devStatus: 'Pending RMA' },
  { key: 'with_manufacturer', label: 'With manufacturer', timelineLabel: 'Dispatched',          step: 2, tone: 'info',    devStatus: 'With Manufacturer' },
  { key: 'returned_to_bench', label: 'Returned to bench', timelineLabel: 'Returned to bench',   step: 3, tone: 'brand',   devStatus: 'In Testing' },
  { key: 'closed',            label: 'Closed',            timelineLabel: 'Closed',              step: 4, tone: 'success', devStatus: null },
];

export const RMA_STAGE = Object.fromEntries(RMA_STAGES.map(s => [s.key, s]));
export const OPEN_STAGES = ['awaiting_dispatch', 'with_manufacturer', 'returned_to_bench'];

// Stages that no longer exist, mapped to the stage that replaced them. Read by stageOf so a
// record written before a stage was retired still lands somewhere real — without this an
// old `in_transit` shipment would match no queue tab and silently disappear from the UI.
export const RETIRED_STAGES = {
  in_transit: 'with_manufacturer',
};

// Legacy 3-status model <-> new 4-stage model. LEGACY_STAGE is used once, by the mergeState
// migration. LEGACY_STATUS is written alongside `stage` by every transition so the readers
// that still key off `status` keep working until they are migrated.
export const LEGACY_STAGE = {
  preparing: 'awaiting_dispatch',
  dispatched: 'with_manufacturer',
  returned: 'returned_to_bench',
};
export const LEGACY_STATUS = {
  awaiting_dispatch: 'preparing',
  with_manufacturer: 'dispatched',
  returned_to_bench: 'returned',
  closed: 'returned',
};

// Read a shipment's stage tolerantly: post-migration records have `stage`, but a record
// written by an older tab (or read before the first post-migration save lands) may only
// have `status`.
export const stageOf = (sh) => {
  const raw = sh?.stage || LEGACY_STAGE[sh?.status] || 'awaiting_dispatch';
  return RETIRED_STAGES[raw] || raw;
};
export const isOpen = (sh) => OPEN_STAGES.includes(stageOf(sh));

// ── SLA / age ───────────────────────────────────────────────────────────────────────────
// Three distinct numbers that are easy to conflate:
//   stageAgeDays — time in the CURRENT stage (the queue's "In stage" column)
//   daysWithManufacturer / isOverdue / daysOverSla — time since DISPATCH (the SLA pill)

export const DEFAULT_SLA_DAYS = 30;

export const stageAgeDays = (sh, now = Date.now()) => {
  const from = sh?.stageEnteredAt || sh?.dispatchedAt || sh?.createdAt;
  if (!from) return 0;
  const d = new Date(from);
  if (isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((now - d.getTime()) / DAY_MS));
};

export const daysWithManufacturer = (sh, now = Date.now()) => {
  if (!sh?.dispatchedAt) return null;
  const d = new Date(sh.dispatchedAt);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((now - d.getTime()) / DAY_MS));
};

export const slaDaysOf = (sh, settings) =>
  sh?.slaDays ?? settings?.slaDays ?? DEFAULT_SLA_DAYS;

// Overdue only applies while the unit is actually away. A returned or closed RMA is never
// overdue, however long it took.
export const isOverdue = (sh, now = Date.now(), settings) => {
  const stage = stageOf(sh);
  if (stage !== 'with_manufacturer') return false;
  const days = daysWithManufacturer(sh, now);
  return days !== null && days > slaDaysOf(sh, settings);
};

export const daysOverSla = (sh, now = Date.now(), settings) => {
  const days = daysWithManufacturer(sh, now);
  if (days === null) return 0;
  return Math.max(0, days - slaDaysOf(sh, settings));
};

// Median of (returnedAt - dispatchedAt) over RMAs returned in the window. Returns null, not
// 0, when there is no history — the tile must render "—" rather than implying a real zero.
export const medianTurnaroundDays = (shipments, windowDays = 90, now = Date.now()) => {
  const cutoff = now - windowDays * DAY_MS;
  const spans = (shipments || [])
    .filter(sh => sh?.dispatchedAt && sh?.returnedAt)
    .filter(sh => new Date(sh.returnedAt).getTime() >= cutoff)
    .map(sh => (new Date(sh.returnedAt).getTime() - new Date(sh.dispatchedAt).getTime()) / DAY_MS)
    .filter(n => !isNaN(n) && n >= 0)
    .sort((a, b) => a - b);
  if (!spans.length) return null;
  const mid = Math.floor(spans.length / 2);
  return Math.round(spans.length % 2 ? spans[mid] : (spans[mid - 1] + spans[mid]) / 2);
};

// Devices currently out of stock because of an RMA, across all open shipments.
export const unitsAwayFromStock = (shipments) => {
  const ids = new Set();
  (shipments || []).filter(isOpen).forEach(sh => (sh.deviceIds || []).forEach(id => ids.add(id)));
  return ids.size;
};

// ── Refs ────────────────────────────────────────────────────────────────────────────────
// Human-readable RMA-<yyyy>-<nnn>. Backed by a stored counter per year (st.rmaCounters)
// rather than a max-scan over the array, so a ref is never reused even if a record is
// deleted. Mint inside the setSt updater so two rapid creates cannot collide.

export const RMA_REF_PREFIX = 'RMA-';

export const formatRmaRef = (year, num) => RMA_REF_PREFIX + year + '-' + String(num).padStart(3, '0');

export const parseRmaRef = (ref) => {
  const m = typeof ref === 'string' && ref.match(/^RMA-(\d{4})-(\d+)$/);
  return m ? { year: Number(m[1]), num: Number(m[2]) } : null;
};

// Pure: returns the next ref plus an updated counters object. The caller writes `counters`
// back into st in the same update that appends the shipment.
export const mintRmaRef = (counters, now = new Date()) => {
  const year = now.getFullYear();
  const next = ((counters || {})[year] || 0) + 1;
  return {
    ref: formatRmaRef(year, next),
    refNum: next,
    refYear: year,
    counters: { ...(counters || {}), [year]: next },
  };
};

// ── Outcomes ────────────────────────────────────────────────────────────────────────────
// What the manufacturer did, and where the unit goes as a result.
//
// destLabel is the design's wording, kept for display. devStatus is what actually drives
// device state, and it deliberately differs from the mockup: every non-scrapped outcome
// lands the unit in "In Testing" awaiting an optional bench test, rather than going
// straight back to stock. "What Lightbug says it did" and "whether it works" are two
// different facts, and only the second one should put a unit back into circulation.

export const RMA_OUTCOMES = [
  { key: 'repaired',       label: 'Repaired',       devStatus: 'In Testing', destLabel: 'Back to stock · bench test', tone: 'success', needsBenchTest: true },
  { key: 'replaced',       label: 'Replaced',       devStatus: 'In Testing', destLabel: 'Back to stock · new serial', tone: 'success', needsBenchTest: true, needsNewSerial: true },
  { key: 'no_fault_found', label: 'No fault found', devStatus: 'In Testing', destLabel: 'Re-test queue',              tone: 'warning', needsBenchTest: true },
  { key: 'scrapped',       label: 'Scrapped',       devStatus: 'Retired',    destLabel: 'Write-off',                  tone: 'danger',  needsBenchTest: false },
];

export const RMA_OUTCOME = Object.fromEntries(RMA_OUTCOMES.map(o => [o.key, o]));

// A scrapped unit is disposed of by the manufacturer, so it is retired directly rather
// than going through the st.retirementRequests approval flow. "Warranty Return" is already
// in RETIRE_REASONS, so retirement reporting stays coherent.
export const SCRAPPED_RETIRE_REASON = 'Warranty Return';

// True when every device on the shipment has an outcome and none is still awaiting a bench
// test — i.e. the RMA can be closed.
export const canCloseRma = (sh) => {
  const ids = sh?.deviceIds || [];
  if (!ids.length) return false;
  const outcomes = sh.outcomes || {};
  return ids.every(id => {
    const o = outcomes[id];
    if (!o || !o.outcome) return false;
    return !RMA_OUTCOME[o.outcome]?.needsBenchTest || o.benchTested;
  });
};

// Devices on this shipment that were received, are not scrapped, and have not yet been
// bench tested — the subset the return-test wizard should cover.
export const awaitingBenchTest = (sh) => {
  const outcomes = sh?.outcomes || {};
  return (sh?.deviceIds || []).filter(id => {
    const o = outcomes[id];
    return o && o.outcome && RMA_OUTCOME[o.outcome]?.needsBenchTest && !o.benchTested;
  });
};

// ── Tones ───────────────────────────────────────────────────────────────────────────────
// Every colour in the approved design turned out to already be a token, so tones map
// straight onto CSS custom properties and both themes work with no branching.

export const TONE = {
  success: { fill: 'var(--success-fill)', text: 'var(--success-text)', border: 'var(--success-border)', pill: 'pill-success' },
  danger:  { fill: 'var(--danger-fill)',  text: 'var(--danger-text)',  border: 'var(--danger-border)',  pill: 'pill-danger' },
  warning: { fill: 'var(--warning-fill)', text: 'var(--warning-text)', border: 'var(--warning-border)', pill: 'pill-warning' },
  info:    { fill: 'var(--info-fill)',    text: 'var(--info-text)',    border: 'var(--info-border)',    pill: 'pill-info' },
  brand:   { fill: 'var(--brand-tint)',   text: 'var(--brand-light)',  border: 'var(--brand-border)',   pill: 'pill-brand' },
  neutral: { fill: 'var(--surface-inset)', text: 'var(--text-muted)',  border: 'var(--border-subtle)',  pill: 'pill-neutral' },
};

// Age thresholds from the design: over 60 days is red, over 30 amber.
export const ageTone = (days) => (days > 60 ? 'danger' : days > 30 ? 'warning' : 'neutral');

// ── Activity ────────────────────────────────────────────────────────────────────────────

export const rmaActivityEntry = (kind, text, by, at = new Date().toISOString()) => ({ at, by, kind, text });

// Historical shipments predate the `activity` array, so synthesise one from the timestamp
// triples they do have. Keeps the detail screen's Activity card from being blank for every
// pre-redesign RMA.
export const deriveActivityFromTimestamps = (sh) => {
  const out = [];
  const n = (sh?.deviceIds || []).length;
  if (sh?.createdAt) out.push(rmaActivityEntry('raised', 'RMA raised with ' + n + (n === 1 ? ' unit' : ' units'), sh.createdBy || '', sh.createdAt));
  if (sh?.dispatchedAt) {
    const via = [sh.carrier, sh.trackingNumber].filter(Boolean).join(' · ');
    out.push(rmaActivityEntry('dispatched', 'Dispatched' + (via ? ' via ' + via : ''), sh.dispatchedBy || '', sh.dispatchedAt));
  }
  if (sh?.returnedAt) out.push(rmaActivityEntry('received', 'Returned from manufacturer', sh.returnedBy || '', sh.returnedAt));
  return out.sort((a, b) => new Date(b.at) - new Date(a.at));
};

export const ACTIVITY_DOT = {
  raised: 'var(--text-ghost)',
  dispatched: 'var(--brand-default)',
  received: 'var(--info-text)',
  chased: 'var(--warning-text)',
  closed: 'var(--success-text)',
  staged: 'var(--text-ghost)',
  updated: 'var(--text-subtle)',
};

// ── Recorded faults ─────────────────────────────────────────────────────────────────────
// A unit arriving at the New RMA screen has usually already had its fault diagnosed — by
// goods-inwards QC, by the bench test that quarantined it, or by the tech working its
// maintenance job. Re-picking a category by hand is both a wasted click and a chance to
// disagree with the record, so the scan reads the fault back off the device.
//
// This is the same join stageForRma does in App.js, generalised to work from a bare scan
// (where there is no jobId to hand) and to say WHERE the fault came from, since the
// operator needs to see that to trust it.
//
// Sources are ordered most-recently-diagnosed first. An open maintenance job wins over the
// quarantine record that spawned it: the tech may have narrowed "Buttons, Haptics" down to
// just the button, and their finding is the one worth claiming on. quarantineHistory is
// deliberately NOT read — a fault the unit has since come back from is not this RMA's
// fault, and inheriting it would silently claim for a repair that already happened.

// The category vocabulary is shared with QC (see RmaModule's faultCategories), so a failed
// check label usually IS a category. "Usually" because an operator-added category can be
// removed from the picker while old records keep it, and a QC checklist can be edited. Match
// case-insensitively against the live picker and fall back to null rather than inventing a
// chip that does not exist.
const matchCategory = (label, categories) => {
  const want = String(label || '').trim().toLowerCase();
  if (!want) return null;
  const hit = (categories || []).find(c => String(c.label).toLowerCase() === want);
  return hit ? hit.label : null;
};

// The fault a device already carries on record, or null when nothing is recorded.
//
//   { category, checks, note, sourceKey, sourceLabel, description }
//
// `category` is the first failed check that matches a live picker category — the same guess
// stageForRma makes. `checks` keeps the full list so the UI can show what else failed, and
// `description` is the prefilled verbatim text, in the shape QC and stageForRma both build.
export const recordedFault = (device, { categories = [], maintenanceJobs = [] } = {}) => {
  if (!device) return null;

  const openJob = (maintenanceJobs || [])
    .filter(j => j.deviceId === device.id && (j.status === 'open' || j.status === 'in_progress'))
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];

  const candidates = [
    openJob && {
      checks: openJob.failedChecks || [],
      note: openJob.quarantineNote || '',
      sourceKey: 'maintenance',
      sourceLabel: 'open maintenance job',
    },
    // QC and field quarantine both write quarantineFailedChecks/quarantineNote, so the
    // device's own status is what distinguishes them. Pending RMA + a fail on the QC record
    // means the unit never left goods-inwards.
    device.qc?.result === 'fail' && {
      checks: device.qc.failedChecks || device.quarantineFailedChecks || [],
      note: device.qc.note || device.quarantineNote || '',
      sourceKey: 'qc',
      sourceLabel: device.importBatch ? 'goods-inwards QC · ' + device.importBatch : 'goods-inwards QC',
    },
    (device.quarantineFailedChecks || []).length || device.quarantineNote ? {
      checks: device.quarantineFailedChecks || [],
      note: device.quarantineNote || '',
      sourceKey: 'quarantine',
      sourceLabel: 'field quarantine',
    } : null,
  ].filter(Boolean);

  // A note with no failed checks is still a recorded fault — several import paths write one
  // (QC's manual "Mark as Failed", the dead-on-arrival path) with an empty checks array.
  // It cannot pick a category, but it can prefill the description, which is the half that
  // goes to the manufacturer verbatim.
  const found = candidates.find(c => c.checks.length || c.note);
  if (!found) return null;

  return {
    ...found,
    category: found.checks.map(c => matchCategory(c, categories)).find(Boolean) || null,
    description: [found.checks.join(', '), found.note].filter(Boolean).join(' — '),
  };
};
